import React, { useEffect, useState } from 'react';

import {
  Skeleton,
} from '@mui/material';

import LessonDetails from '../../../components/LessonDetails';
import SkeletonStack from '../../../components/SkeletonStack';
import StudentRow from '../../../components/StudentRow';
import {
  useGetLessonEnrolmentQuery,
  useGetLessonQuery,
  useRescheduleLessonMutation,
  useUpdateLessonEnrolmentMutation,
  useUpdateLessonMutation,
} from '../../../services/classes';
import { Lesson } from '../../../services/classes/types';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetStaffQuery } from '../../../services/staff';
import { useGetStudentQuery } from '../../../services/students';
import { AttendanceType, EnrolmentType } from '../../../types/common';
import {
  getLessonClass,
  getLessonSubject,
} from '../../../utils/common/classes';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';

interface LessonStudentRowProps {
  studentId: string,
  isOriginalStudent: boolean,
  lesson: Lesson,
}

const LessonStudentRow = ({ studentId,
  isOriginalStudent,
  lesson }: LessonStudentRowProps) => {
  const { data: student, isLoading } = useGetStudentQuery({ studentId });

  const [updateLessonEnrolment] = useUpdateLessonEnrolmentMutation();

  const subject = getLessonSubject(lesson);
  const classId = getLessonClass(lesson);

  const { data: lessonEnrolment,
    isLoading: isLessonEnrolmentLoading } = useGetLessonEnrolmentQuery({
    subject,
    classId,
    lesson: lesson.lesson,
    studentId,
  });

  const handleToggleStudentOnline = (id: string) => {
    updateLessonEnrolment({
      subject,
      classId,
      lesson: lesson.lesson,
      studentId: id,
      newAttributes: {
        attendanceType: lessonEnrolment?.data.attendanceType === AttendanceType.Online ?
          AttendanceType.InPerson : AttendanceType.Online,
      },
    });
  };

  if (isLoading || isLessonEnrolmentLoading) {
    return (
      <Skeleton />
    );
  }

  if (student?.data && lessonEnrolment?.data) {
    return (
      <StudentRow
        student={student.data}
        studentDetailsHref={`/admin/student/${student.data.studentId}`}
        rollActive={false}
        isPresent={lessonEnrolment.data.present}
        isMakeup={lessonEnrolment.data.enrolmentMethod === EnrolmentType.Makeup}
        isOnline={lessonEnrolment.data.attendanceType === AttendanceType.Online}
        onOnlineToggle={isOriginalStudent ? handleToggleStudentOnline : undefined}
      />
    );
  }
  return (
    <div />
  );
};

interface RescheduleLessonDetailsProps {
  studentId: string;
  selectedLesson: Lesson;
  originalLesson: Lesson;
  onClose: () => void;
}

const RescheduleLessonDetails = ({
  selectedLesson,
  originalLesson,
  studentId,
  onClose = () => {},
}: RescheduleLessonDetailsProps) => {
  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});
  const { data: teacher, isLoading: isTeacherLoading } = useGetStaffQuery({
    staffId: selectedLesson.teacher,
  });

  const [rescheduleLesson, { isLoading: isRescheduleLoading,
    isError: isRescheduleError,
    error: rescheduleError,
    isSuccess: rescheduleSuccess }] = useRescheduleLessonMutation();
  const [updateLesson] = useUpdateLessonMutation();

  const subject = getLessonSubject(selectedLesson);
  const classId = getLessonClass(selectedLesson);

  const { data: fetchedLesson,
    isLoading: isLessonLoading } = useGetLessonQuery({
    subject, classId, lesson: selectedLesson.lesson,
  });

  const [lesson, setLesson] = useState<Lesson>(selectedLesson);
  const [makeUpScheduled, setMakeUpScheduled] = useState(false);

  useEffect(() => {
    if (fetchedLesson) {
      setLesson(fetchedLesson.data);
      // Make up is scheduled if the student is enrolled in the lesson
      setMakeUpScheduled(!!fetchedLesson.data.enrolledStudents
        .find((e) => e.studentId === studentId));
    }
  }, [fetchedLesson]);

  const handleMakeUp = () => {
    if (makeUpScheduled) {
      // If the makeup lesson has already been scheduled,
      // clicking the reschedule button undoes the makeup
      rescheduleLesson({
        subject,
        oldClassId: classId,
        newClassId: getLessonClass(originalLesson),
        oldLessonId: lesson.lesson,
        newLessonId: originalLesson.lesson,
        studentId,
      });
    } else {
      // Otherwise, execute the makeup scheduling
      rescheduleLesson({
        subject,
        oldClassId: getLessonClass(originalLesson),
        newClassId: classId,
        oldLessonId: originalLesson.lesson,
        newLessonId: lesson.lesson,
        studentId,
      });
    }
  };

  useError(isRescheduleError, rescheduleError);
  useSuccess(rescheduleSuccess, 'Student rescheduled');

  if (isLessonLoading || isCoursesLoading || isTeacherLoading) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <LessonDetails
      lesson={lesson}
      classDetailsHref={`/admin/class/${getLessonSubject(lesson)}/${getLessonClass(lesson)}`}
      courses={courses?.data ?? []}
      teacher={teacher?.data}
      onClose={onClose}
      renderStudentRow={(id) => (
        <LessonStudentRow
          key={id}
          studentId={id}
          isOriginalStudent={id === studentId}
          lesson={lesson}
        />
      )}
      disableTimeEdit
      updateLesson={(newAttributes) => updateLesson(
        {
          subject, classId, lesson: selectedLesson.lesson, newAttributes,
        },
      )}
      scheduleMakeup={handleMakeUp}
      makeUpScheduled={makeUpScheduled}
      isMakeupLoading={isRescheduleLoading}
    />
  );
};

export default RescheduleLessonDetails;
